"use client";

import MultipleEmailsField from "@/components/RHFFields/MultipleEmailsField";
import { Form } from "@/components/ui/form";
import useProfile from "@/hooks/use-profile";
import useApi from "@/hooks/useApi";
import SettingsRepository from "@/utilities/repositories/Settings";
import UserRepository from "@/utilities/repositories/Users";
import { TAgencySetting } from "@/utilities/types/Setting";
import { IResponseData } from "@/utilities/types/requests";
import cn from "@/utils/class-names";
import {
  EditSubUserSettingSchema,
  editSubUserSettingSchema,
} from "@/utils/validators/edit-sub-user-setting.schema";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Input, Switch, Tab, Title } from "rizzui";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";

// Define tab indices
const TabIndices = {
  LEADS: "LEADS",
};

export default function EditSubUserSetting() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { profile } = useProfile();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState<string>(TabIndices.LEADS);

  const DEFAULT_VALUES: EditSubUserSettingSchema = {
    id: "",
    agencyEmail: "",
    additionalEmail: [],
    privyrUrl: "",
    privyrEnabled: false,
  };

  const [reset, setReset] = useState<EditSubUserSettingSchema>(DEFAULT_VALUES);

  const onSubmit: SubmitHandler<EditSubUserSettingSchema> = async (
    formData
  ) => {
    if (!formData?.id) {
      toast.error("Website setting not found");
      navigate(routes.home);
      return;
    }

    try {
      setIsLoading(true);

      // Update additional emails
      if (formData && formData?.additionalEmail?.length > 0) {
        const additionalEmail = formData?.additionalEmail
          .map((item) => item.email)
          .filter((item) => {
            const mainEmail = formData?.agencyEmail;
            return item !== mainEmail;
          });

        await UserRepository.updateMe({
          additionalEmail,
        });
      }

      // Update Privyr settings
      await SettingsRepository.updateAgencySetting(formData?.id || "", {
        privyrUrl: formData?.privyrUrl || "",
        privyrEnabled: !!formData?.privyrEnabled,
      });

      onFetchAgencySetting();
      toast.success(`Updated lead settings successfully`);
      setIsLoading(false);
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
      setIsLoading(false);
    }
  };

  const { request: getAgencySetting, response: agencySettingData } = useApi<
    IResponseData<TAgencySetting>
  >({
    request: SettingsRepository.getAgencySetting,
  });

  const onFetchAgencySetting = () =>
    getAgencySetting()
      .then(async (res) => {
        if (!res?.data) return;

        const {
          id,
          agencyEmail,
          user: { email, additionalEmail },
          privyrEnabled,
          privyrUrl,
        } = res.data;

        const additionalEmailConvert = additionalEmail?.length
          ? additionalEmail.map((email) => ({ email }))
          : [];

        setReset({
          id,
          agencyEmail: agencyEmail || email || "",
          additionalEmail: [
            {
              email: agencyEmail || email || "",
            },
            ...additionalEmailConvert,
          ],
          privyrUrl: privyrUrl || "",
          privyrEnabled: !!privyrEnabled,
        });
      })
      .catch(() => {
        toast.error("Failed to load website settings");
        navigate(routes.home);
      });

  useEffect(() => {
    onFetchAgencySetting();
  }, []);

  const [form, setForm] = useState<unknown>(null);

  // Available tabs
  const availableTabs = [{ name: "Leads", index: TabIndices.LEADS }];

  // Handle tab change
  const handleTabChange = (index: number) => {
    const tab = availableTabs[index];
    if (tab) setTabIndex(tab.index);
  };

  return (
    <Form<EditSubUserSettingSchema>
      resetValues={reset}
      onSubmit={onSubmit}
      validationSchema={editSubUserSettingSchema}
      className="grid grid-cols-1 @container md:grid-cols-2 [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900 mt-4"
      onInitForm={setForm}
    >
      {({
        control,
        register,
        watch,
        reset,
        setValue,
        formState: { errors, defaultValues, dirtyFields, isDirty },
        setError,
      }) => {
        return (
          <>
            <div className="relative z-50 mb-2 flex flex-wrap items-center justify-between gap-2.5 @container">
              <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
                Website Settings
              </Title>
            </div>

            <Tab
              className="col-span-full"
              selectedIndex={availableTabs.findIndex(
                (tab) => tab.index === tabIndex
              )}
              onChange={handleTabChange}
            >
              <Tab.List>
                {availableTabs.map((tab) => (
                  <Tab.ListItem key={tab.name}>{tab.name}</Tab.ListItem>
                ))}
              </Tab.List>

              <Tab.Panels>
                {availableTabs.map((tab) => {
                  switch (tab.index) {
                    case TabIndices.LEADS:
                      return (
                        <Tab.Panel
                          key={tab.name}
                          data-name="leads"
                          className="flex flex-col gap-6"
                        >
                          <MultipleEmailsField
                            name="additionalEmail"
                            agencyDefaultEmail={
                              defaultValues?.agencyEmail || ""
                            }
                            control={control}
                            register={register}
                            errors={errors}
                          />

                          <Controller
                            control={control}
                            name={"privyrUrl"}
                            render={({
                              field: formField,
                              fieldState,
                              formState,
                            }) => {
                              return (
                                <Input
                                  label="Webhook Privyr Url"
                                  placeholder="Enter Privyr Url to connect"
                                  {...formField}
                                  value={formField.value}
                                  error={fieldState.error?.message}
                                />
                              );
                            }}
                          />

                          <Controller
                            control={control}
                            name={"privyrEnabled"}
                            render={({ field, fieldState, formState }) => {
                              return (
                                <Switch
                                  label="Enable"
                                  size="lg"
                                  className="w-auto"
                                  checked={field.value || false}
                                  onChange={field.onChange}
                                  error={fieldState.error?.message}
                                />
                              );
                            }}
                          />
                        </Tab.Panel>
                      );

                    default:
                      return null;
                  }
                })}
              </Tab.Panels>
            </Tab>

            <div
              className={cn(
                "col-span-full -mb-8 flex items-center justify-end gap-4 bg-white p-2 sticky bottom-0 left-0 right-0 z-[99]"
              )}
            >
              <Button
                type="button"
                variant="outline"
                className="w-full @xl:w-auto"
                onClick={() => reset()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                id={"form-sub-user-settings"}
                isLoading={isLoading}
                className="w-full @xl:w-auto"
                disabled={!isDirty}
              >
                Update
              </Button>
            </div>
          </>
        );
      }}
    </Form>
  );
}
